import { useState, useEffect, useMemo } from 'react';
import { 
  Typography, 
  Card,
  Space,
  Button, 
  Table, 
  Input,
  Tag,
  message,
  Spin,
  Avatar,
  Descriptions,
  List,
  Modal,
  Form,
  Popconfirm
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { 
  UserOutlined,
  SearchOutlined,
  EyeOutlined,
  ShoppingOutlined,
  BankOutlined,
  MailOutlined,
  UserAddOutlined,
  EditOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { supabase } from '../../lib/supabase';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import CreateUserModal from '../../components/admin/CreateUserModal';

const { Title } = Typography;

// Combined interface for company data
interface Company {
  id: string;
  type: 'company' | 'client';
  name: string; // bedrijfsnaam for companies, email for individual clients
  email?: string;
  created_at: string;
  last_active?: string | null;
  address?: string;
  city?: string;
  postal_code?: string;
  kvk?: string;
  btw?: string;
  contact_person?: string;
  phone?: string;
  product_count: number;
  users?: User[];
  metadata?: any;
}

interface User {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string | null;
}

interface CompaniesProps {
  viewSingle?: boolean;
  editMode?: boolean;
  createMode?: boolean;
}

export default function Companies({ viewSingle, editMode, createMode }: CompaniesProps) {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [currentCompany, setCurrentCompany] = useState<Company | null>(null);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [createUserModalVisible, setCreateUserModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();

  // Check if the screen is mobile size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (viewSingle && id) {
      fetchCompanyById(id);
    } else {
      fetchCompanies();
    }
  }, [viewSingle, id]);

  async function fetchCompanies() {
    setLoading(true);
    try {
      // Fetch companies
      const { data: companiesData, error: companiesError } = await supabase
        .from('companies')
        .select('*');
      
      if (companiesError) throw companiesError;
      
      // Log first company for debugging
      if (companiesData && companiesData.length > 0) {
        console.log('Sample company data:', companiesData[0]);
      }
      
      // Get client product counts directly from product_access
      const { data: productAccessData, error: productAccessError } = await supabase
        .from('product_access')
        .select('user_id');
      
      if (productAccessError) {
        console.error('Error fetching product access:', productAccessError);
      }
      
      // Calculate product counts per user
      const productCountMap: Record<string, number> = {};
      productAccessData?.forEach(access => {
        if (!productCountMap[access.user_id]) {
          productCountMap[access.user_id] = 0;
        }
        productCountMap[access.user_id]++;
      });
      
      // Fetch all users for company association
      let users: any[] = [];
      const { data: usersData, error: usersError } = await supabase.rpc('admin_get_all_users');
      
      if (usersError) {
        console.error('Error fetching users:', usersError);
      } else {
        users = usersData || [];
        // Filter out any test users
        users = users.filter(user => 
          !user.email.includes('test') && 
          !user.email.includes('example.com'));
      }
      
      // We can't get user metadata from client-side admin API
      console.log('Skipping metadata fetch as it requires server-side admin access');
      
      // Create empty metadata map for contact info
      const userMetadataMap: Record<string, any> = {};

      // Transform companies data and associate users
      const transformedCompanies: Company[] = companiesData?.map(company => {
        // Find associated users for this company
        const companyUsers = users.filter(user => user.id === company.user_id);
        
        // Get product count for this company
        const productCount = productCountMap[company.user_id] || 0;
        
        // Get user metadata if available
        const userMetadata = userMetadataMap[company.user_id] || {};
        
        // Ensure contact_person and phone are correctly fetched
        const contactPerson = 
          // First try from user metadata
          (userMetadata.voornaam && userMetadata.achternaam ? 
            `${userMetadata.voornaam} ${userMetadata.achternaam}`.trim() : 
            // Then try from company fields
            company.contactpersoon || 
            (company.voornaam && company.achternaam ? 
              `${company.voornaam} ${company.achternaam}`.trim() : 
              // Then try from financial contact (if nothing else)
              (company.voornaam_financieel && company.achternaam_financieel ? 
                `${company.voornaam_financieel} ${company.achternaam_financieel}`.trim() :
                '')));
                              
        // Get phone from user metadata or company data
        const phone = userMetadata.telefoon || company.telefoon || company.telefoon_financieel || '';
        
        return {
          id: company.id,
          type: 'company',
          name: company.bedrijfsnaam,
          email: company.email || companyUsers[0]?.email,
          created_at: company.created_at,
          address: company.adres,
          city: company.stad,
          postal_code: company.postcode,
          kvk: company.kvk_nummer,
          btw: company.btw_nummer,
          contact_person: contactPerson,
          phone: phone,
          product_count: productCount,
          users: companyUsers,
          metadata: { ...company, user_metadata: userMetadata }
        };
      }) || [];
      
      // Set companies
      setCompanies(transformedCompanies);
    } catch (error) {
      console.error('Error fetching data:', error);
      message.error('Fout bij het ophalen van bedrijfsgegevens');
    } finally {
      setLoading(false);
    }
  }

  async function fetchCompanyById(companyId: string) {
    setLoading(true);
    try {
      // Try to fetch as company first
      const { data: company, error: companyError } = await supabase
        .from('companies')
        .select('*')
        .eq('id', companyId)
        .maybeSingle();

      if (company) {
        // Debug: Log company data to check structure
        console.log('Company data from database:', company);
        
        // It's a company
        // Get product count directly from product_access
        const { data: productAccessData, error: productAccessError } = await supabase
          .from('product_access')
          .select('id')
          .eq('user_id', company.user_id);

        const productCount = productAccessData?.length || 0;

        // We can't use admin API directly from the client
        console.log('Using RPC function to get user data instead of admin API');
        
        let users: User[] = [];
        let userMetadata: any = {};
        
        // Get user data using the RPC function that's allowed for client use
        const { data: clientsData } = await supabase.rpc('admin_get_all_users');
        
        if (clientsData) {
          const user = clientsData.find((u: any) => u.id === company.user_id);
          if (user) {
            users = [user];
            console.log('User data from RPC:', user);
            
            // We can't get additional metadata from client side
            console.log('Using basic user data without additional metadata');
          }
        }

        // Extract contact person info from company data since we can't get user metadata
        const contactPerson = 
          company.contactpersoon || 
          (company.voornaam && company.achternaam ? 
            `${company.voornaam} ${company.achternaam}`.trim() : 
            (company.voornaam_financieel && company.achternaam_financieel ? 
              `${company.voornaam_financieel} ${company.achternaam_financieel} (financieel)`.trim() :
              ''));
                              
        // Get phone from company data
        const phone = company.telefoon || company.telefoon_financieel || '';

        const orgData: Company = {
          id: company.id,
          type: 'company',
          name: company.bedrijfsnaam,
          email: company.email || users[0]?.email,
          created_at: company.created_at,
          address: company.adres,
          city: company.stad || company.plaats,
          postal_code: company.postcode,
          kvk: company.kvk_nummer || company.kvk,
          btw: company.btw_nummer || company.btw,
          contact_person: contactPerson,
          phone: phone,
          product_count: productCount,
          users: users,
          metadata: { ...company }
        };

        // Debug: Log transformed company data
        console.log('Transformed company data:', orgData);

        setCurrentCompany(orgData);
        setLoading(false);
        return;
      }

      // If not found as company, try to fetch as client
      // Try to find the user in our client data
      const { data: clientsData } = await supabase.rpc('admin_get_all_users');
        
      if (clientsData) {
        const client = clientsData.find((c: any) => c.id === companyId);
        
        if (client) {
          // Get product count
          const { count: productCount } = await supabase
            .from('product_access')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', client.id);

          const orgData: Company = {
            id: client.id,
            type: 'client',
            name: client.email,
            email: client.email,
            created_at: client.created_at,
            last_active: client.last_sign_in_at,
            product_count: productCount || 0,
            metadata: client
          };

          setCurrentCompany(orgData);
          setLoading(false);
          return;
        }
      }

      message.error('Klant niet gevonden');
      navigate('/admin/companies');
    } catch (error) {
      console.error('Error fetching organization details:', error);
      message.error('Fout bij het ophalen van klantgegevens');
    } finally {
      setLoading(false);
    }
  }

  async function handleResendConfirmationEmail(company: Company) {
    setSelectedCompany(company);
    Modal.confirm({
      title: 'Bevestigingsmail verzenden',
      content: `Weet u zeker dat u een nieuwe bevestigingsmail wilt sturen naar ${company.email}?`,
      onOk: async () => {
        try {
          setResendingEmail(true);
          
          // Use the Supabase auth API directly - this will send a magic link email
          const { data, error } = await supabase.auth.resend({
            type: 'signup',
            email: company.email || '',
            options: {
              emailRedirectTo: `${window.location.origin}/auth/callback`
            }
          });
          
          if (error) {
            console.error('Supabase error:', error);
            message.error('Verzenden van bevestigingsmail mislukt');
          } else {
            message.success(`Bevestigingsmail verzonden naar ${company.email}`);
          }
        } catch (error: any) {
          console.error('Error resending confirmation email:', error);
          message.error('Verzenden van bevestigingsmail mislukt');
        } finally {
          setResendingEmail(false);
          setSelectedCompany(null);
        }
      },
      onCancel: () => {
        setSelectedCompany(null);
      }
    });
  }

  const handleCreateUserSuccess = () => {
    setCreateUserModalVisible(false);
    fetchCompanies(); // Refresh the company list
    message.success('Gebruiker succesvol aangemaakt');
  };

  const handleDeleteCompany = async (company: Company) => {
    try {
      const response = await fetch('/api/admin/clients', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify({ id: company.metadata?.user_id || company.users?.[0]?.id })
      });

      if (!response.ok) {
        throw new Error('Failed to delete client');
      }

      message.success('Klant succesvol verwijderd');
      fetchCompanies(); // Refresh the company list
    } catch (error) {
      console.error('Error deleting client:', error);
      message.error('Fout bij het verwijderen van klant');
    }
  };

  const handleEditCompany = (company: Company) => {
    setEditingCompany(company);
    // Access the raw database data from metadata
    const rawCompanyData = company.metadata || {};
    
    form.setFieldsValue({
      bedrijfsnaam: rawCompanyData.bedrijfsnaam || company.name,
      email: company.email,
      voornaam: rawCompanyData.voornaam_financieel || '',
      achternaam: rawCompanyData.achternaam_financieel || '',
      telefoon: rawCompanyData.telefoon_financieel || company.phone,
      adres: rawCompanyData.adres || company.address,
      postcode: rawCompanyData.postcode || company.postal_code,
      stad: rawCompanyData.stad || company.city,
      kvk_nummer: rawCompanyData.kvk_nummer || company.kvk,
      btw_nummer: rawCompanyData.btw_nummer || company.btw,
      land: rawCompanyData.land || 'Nederland'
    });
    setEditModalVisible(true);
  };

  const handleUpdateCompany = async (values: any) => {
    if (!editingCompany) return;

    try {
      const response = await fetch('/api/admin/clients', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify({
          id: editingCompany.metadata?.user_id || editingCompany.users?.[0]?.id,
          ...values
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update client');
      }

      message.success('Klant succesvol bijgewerkt');
      setEditModalVisible(false);
      setEditingCompany(null);
      form.resetFields();
      fetchCompanies(); // Refresh the company list
    } catch (error) {
      console.error('Error updating client:', error);
      message.error('Fout bij het bijwerken van klant');
    }
  };

  // Filter companies based on searchTerm
  const filteredCompanies = useMemo(() => {
    let filtered = companies;

    // Filter by search term if provided
    if (searchTerm) {
      filtered = filtered.filter(company => {
        const searchableText = [
          company.name,
          company.email,
          company.address,
          company.city,
          company.postal_code,
          company.contact_person,
          company.kvk,
          company.btw,
          company.phone
        ].filter(Boolean).join(' ').toLowerCase();
        
        return searchableText.includes(searchTerm.toLowerCase());
      });
    }
    
    return filtered;
  }, [companies, searchTerm]);

  const columns: ColumnsType<Company> = [
    {
      title: 'Bedrijfsnaam',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (_, record) => (
        <Space>
          <Avatar 
            icon={<BankOutlined />} 
            style={{ backgroundColor: '#1890ff' }}
          />
          <span>{record.name}</span>
        </Space>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      responsive: ['sm'],
    },
    {
      title: 'Contactpersoon',
      dataIndex: 'contact_person',
      key: 'contact_person',
      responsive: ['md'],
      render: (text) => text || '-',
    },
    {
      title: 'Telefoon',
      dataIndex: 'phone',
      key: 'phone',
      responsive: ['lg'],
      render: (text) => text || '-',
    },
    {
      title: 'Producten',
      dataIndex: 'product_count',
      key: 'product_count',
      sorter: (a, b) => a.product_count - b.product_count,
      render: (count: number) => (
        <Tag color={count > 0 ? 'blue' : 'default'}>
          {count}
        </Tag>
      ),
    },
    {
      title: 'Aangemaakt',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => dayjs(date).format('DD-MM-YYYY'),
      sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      responsive: ['xl'],
    },
    {
      title: 'Acties',
      key: 'actions',
      render: (_, record) => (
        <Space wrap>
          <Button 
            type="link" 
            icon={<EyeOutlined />} 
            onClick={() => navigate(`/admin/companies/${record.id}`)}
            size={isMobile ? 'small' : 'middle'}
            title="Bekijk details"
          />
          <Button 
            type="link" 
            icon={<EditOutlined />} 
            onClick={() => handleEditCompany(record)}
            size={isMobile ? 'small' : 'middle'}
            title="Bewerk klant"
          />
          <Button 
            type="link" 
            icon={<ShoppingOutlined />} 
            onClick={() => navigate(`/admin/companies/${record.id}/products`)}
            size={isMobile ? 'small' : 'middle'}
            title="Beheer producten"
          />
          {record.email && (
            <Button 
              type="link" 
              icon={<MailOutlined />} 
              onClick={() => handleResendConfirmationEmail(record)}
              size={isMobile ? 'small' : 'middle'}
              loading={resendingEmail && selectedCompany?.id === record.id}
              title="Verstuur bevestigingsmail"
            />
          )}
          <Popconfirm
            title="Klant verwijderen"
            description="Weet u zeker dat u deze klant wilt verwijderen? Dit kan niet ongedaan worden gemaakt."
            onConfirm={() => handleDeleteCompany(record)}
            okText="Ja, verwijder"
            cancelText="Annuleren"
            okType="danger"
          >
            <Button 
              type="link" 
              icon={<DeleteOutlined />} 
              danger
              size={isMobile ? 'small' : 'middle'}
              title="Verwijder klant"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Component to display company details
  const CompanyDetails = () => {
    if (!currentCompany) {
      return (
        <div style={{ textAlign: 'center', padding: '30px' }}>
          <Spin size="large" />
        </div>
      );
    }

    // Common actions
    const actions = (
      <Space>
        <Button 
          type="primary" 
          icon={<ShoppingOutlined />}
          onClick={() => navigate(`/admin/companies/${currentCompany.id}/products`)}
        >
          Producten Beheren
        </Button>
      </Space>
    );

    const title = currentCompany.type === 'company' ? 'Bedrijf details' : 'Gebruiker details';

    return (
      <Card style={{ borderRadius: '8px' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Space direction="vertical" size="small">
            <Button 
              type="link" 
              onClick={() => navigate('/admin/companies')}
              style={{ padding: 0 }}
            >
              ← Terug naar klantenoverzicht
            </Button>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Space align="center">
                <Avatar 
                  size={64}
                  icon={currentCompany.type === 'company' ? <BankOutlined /> : <UserOutlined />} 
                  style={{ 
                    backgroundColor: currentCompany.type === 'company' ? '#1890ff' : '#52c41a'
                  }}
                />
                <div>
                  <Title level={4} style={{ margin: 0 }}>
                    {currentCompany.name}
                  </Title>
                  <Tag color={currentCompany.type === 'company' ? 'blue' : 'green'}>
                    {currentCompany.type === 'company' ? 'Bedrijf' : 'Gebruiker'}
                  </Tag>
                </div>
              </Space>
              {actions}
            </div>
          </Space>

          <Descriptions 
            bordered 
            column={{ xxl: 3, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}
            title={title}
          >
            <Descriptions.Item label="Email">
              {currentCompany.email || '-'}
            </Descriptions.Item>

            {currentCompany.type === 'company' && (
              <>
                <Descriptions.Item label="Contactpersoon">
                  {currentCompany.contact_person || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Telefoon">
                  {currentCompany.phone || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Adres">
                  {currentCompany.address ? (
                    <>
                      {currentCompany.address}<br />
                      {currentCompany.postal_code} {currentCompany.city}
                    </>
                  ) : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="KVK Nummer">
                  {currentCompany.kvk || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="BTW Nummer">
                  {currentCompany.btw || '-'}
                </Descriptions.Item>
              </>
            )}

            <Descriptions.Item label="Account aangemaakt">
              {currentCompany.created_at 
                ? dayjs(currentCompany.created_at).format('DD-MM-YYYY HH:mm') 
                : '-'}
            </Descriptions.Item>
            
            {currentCompany.type === 'client' && (
              <Descriptions.Item label="Laatste inlog">
                {currentCompany.last_active 
                  ? dayjs(currentCompany.last_active).format('DD-MM-YYYY HH:mm') 
                  : 'Nooit'}
              </Descriptions.Item>
            )}
            
            <Descriptions.Item label="Producten toegang">
              <Space align="baseline">
                <Tag color={currentCompany.product_count > 0 ? 'blue' : 'default'}>
                  {currentCompany.product_count || 0}
                </Tag>
                <Button 
                  type="link" 
                  icon={<ShoppingOutlined />} 
                  onClick={() => navigate(`/admin/companies/${currentCompany.id}/products`)}
                >
                  Producten beheren
                </Button>
              </Space>
            </Descriptions.Item>
            
            {currentCompany.type === 'company' && currentCompany.users && (
              <Descriptions.Item label="Gebruikers" span={3}>
                <List
                  size="small"
                  bordered
                  dataSource={currentCompany.users}
                  renderItem={(user) => (
                    <List.Item
                      actions={[
                        <Button type="link" size="small">Details</Button>
                      ]}
                    >
                      <List.Item.Meta
                        avatar={<Avatar icon={<UserOutlined />} />}
                        title={user.email}
                        description={`Aangemaakt: ${dayjs(user.created_at).format('DD-MM-YYYY')}`}
                      />
                    </List.Item>
                  )}
                  locale={{ emptyText: 'Geen gebruikers gevonden' }}
                />
              </Descriptions.Item>
            )}
          </Descriptions>
        </Space>
      </Card>
    );
  };

  // If viewing a single company, just show the company detail
  if (viewSingle && id) {
    return (
      <div style={{ padding: '24px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '30px' }}>
            <Spin size="large" />
          </div>
        ) : (
          <CompanyDetails />
        )}
      </div>
    );
  }

  // The main companies listing view
  return (
    <div style={{ padding: '24px' }}>
      <Card
        style={{
          borderRadius: '8px',
        }}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div style={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between', 
            alignItems: isMobile ? 'flex-start' : 'center',
            gap: isMobile ? '16px' : '0'
          }}>
            <Title level={4} style={{ margin: 0 }}>Bedrijven</Title>
            <Space 
              size="middle" 
              wrap={true}
              style={{ 
                width: isMobile ? '100%' : 'auto',
                display: 'flex',
                flexWrap: 'wrap'
              }}
            >
              <Input
                placeholder="Zoek bedrijven..."
                prefix={<SearchOutlined />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ 
                  width: isMobile ? '100%' : 200 
                }}
              />
              <Button 
                type="primary" 
                icon={<UserAddOutlined />}
                onClick={() => setCreateUserModalVisible(true)}
              >
                Nieuwe klant
              </Button>
            </Space>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '30px' }}>
              <Spin size="large" />
            </div>
          ) : (
            <Table
              columns={columns}
              dataSource={filteredCompanies}
              rowKey="id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50'],
                showTotal: (total, range) => `${range[0]}-${range[1]} van ${total} bedrijven`,
              }}
              scroll={{ x: 'max-content' }}
              size={isMobile ? 'small' : 'middle'}
              locale={{ emptyText: 'Geen bedrijven gevonden' }}
            />
          )}
        </Space>
      </Card>

      <CreateUserModal 
        visible={createUserModalVisible}
        onCancel={() => setCreateUserModalVisible(false)}
        onSuccess={handleCreateUserSuccess}
      />

      <Modal
        title="Klant bewerken"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingCompany(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleUpdateCompany}
        >
          <Form.Item
            name="bedrijfsnaam"
            label="Bedrijfsnaam"
            rules={[{ required: true, message: 'Bedrijfsnaam is verplicht' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Email is verplicht' },
              { type: 'email', message: 'Voer een geldig email adres in' }
            ]}
          >
            <Input disabled />
          </Form.Item>

          <div style={{ display: 'flex', gap: '16px' }}>
            <Form.Item
              name="voornaam"
              label="Voornaam"
              style={{ flex: 1 }}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="achternaam"
              label="Achternaam"
              style={{ flex: 1 }}
            >
              <Input />
            </Form.Item>
          </div>

          <Form.Item
            name="telefoon"
            label="Telefoon"
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="adres"
            label="Adres"
          >
            <Input />
          </Form.Item>

          <div style={{ display: 'flex', gap: '16px' }}>
            <Form.Item
              name="postcode"
              label="Postcode"
              style={{ flex: 1 }}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="stad"
              label="Stad"
              style={{ flex: 1 }}
            >
              <Input />
            </Form.Item>
          </div>

          <div style={{ display: 'flex', gap: '16px' }}>
            <Form.Item
              name="kvk_nummer"
              label="KVK Nummer"
              style={{ flex: 1 }}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="btw_nummer"
              label="BTW Nummer"
              style={{ flex: 1 }}
            >
              <Input />
            </Form.Item>
          </div>

          <Form.Item
            name="land"
            label="Land"
            rules={[{ required: true, message: 'Land is verplicht' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item style={{ marginTop: '24px', textAlign: 'right' }}>
            <Space>
              <Button 
                onClick={() => {
                  setEditModalVisible(false);
                  setEditingCompany(null);
                  form.resetFields();
                }}
              >
                Annuleren
              </Button>
              <Button type="primary" htmlType="submit">
                Opslaan
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
} 